// Iter BE — returning-client "What changed?" screen.
// Fast path: one tap on "No significant changes". Otherwise: what changed,
// how long the benefit lasted, quick status updates on previous findings,
// and an optional new treatment priority. Everything is stored on TODAY's
// session (progress_since_last) — previous sessions are never modified.
import { useState } from "react";
import { CheckCircle2, Plus, ArrowRight } from "lucide-react";
import ProgressSinceLast from "./ProgressSinceLast";
import VoiceTextarea from "../VoiceTextarea";

const BENEFIT_CHIPS = ["Still holding", "About a week", "A few days", "1–2 days", "Wore off quickly"];
const STATUSES = [
  { key: "improved", label: "Improved", cls: "bg-sage-500/10 text-sage-700 border-sage-500" },
  { key: "same", label: "Same", cls: "bg-paper-100 text-ink-700 border-ink-400" },
  { key: "worse", label: "Worse", cls: "bg-terracotta-500/10 text-terracotta-700 border-terracotta-500" },
  { key: "resolved", label: "Resolved", cls: "bg-sky-100 text-sky-700 border-sky-500" },
];

const titleize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Candidate items pulled from the PREVIOUS session, split by who can answer.
//  - client:    symptom/complaint-level things the client can actually perceive
//               and rate Improved/Same/Worse/Resolved.
//  - therapist: previously-worked muscle/tissue structures — reassessment
//               REMINDERS only. These are NOT client-ratable and get no status
//               here; real findings are captured later in the clinical workflow.
export const deriveFindingCandidates = (prev) => {
  if (!prev) return { client: [], therapist: [] };
  const client = [];
  const cc = prev.chief_complaint || prev.intake_notes;
  if (cc) client.push(`Chief complaint: ${String(cc).slice(0, 80)}`);
  const therapist = (prev.worked_areas || []).slice(0, 6).map((a) => titleize(a));
  return { client, therapist };
};

export default function WhatChangedSection({ previousSession, progress, onChange, onBlurSave, onNoChanges, painEntered = true, onPainRequired }) {
  const [newFinding, setNewFinding] = useState("");
  const { client: clientCandidates, therapist: reassessCandidates } = deriveFindingCandidates(previousSession);
  const updates = progress?.finding_updates || [];

  const statusOf = (label) => (updates.find((u) => u.label === label) || {}).status || null;

  const setStatus = (label, status) => {
    const rest = updates.filter((u) => u.label !== label);
    const cur = statusOf(label);
    const next = cur === status ? rest : [...rest, { label, status, source: "previous", kind: "client" }];
    onChange({ ...progress, finding_updates: next, no_significant_changes: false });
    onBlurSave?.();
  };

  const addFinding = () => {
    const label = newFinding.trim();
    if (!label) return;
    onChange({
      ...progress,
      finding_updates: [...updates, { label, status: "new", source: "new", kind: "client" }],
      no_significant_changes: false,
    });
    setNewFinding("");
    onBlurSave?.();
  };

  const setField = (key, value) =>
    onChange({ ...progress, [key]: value, ...(value ? { no_significant_changes: false } : {}) });

  return (
    <div className="space-y-6" data-testid="what-changed-section">
      {/* Fast path */}
      <button
        type="button"
        data-testid="no-significant-changes-btn"
        onClick={() => {
          if (!painEntered) { onPainRequired?.(); return; }
          onChange({ ...progress, no_significant_changes: true });
          onNoChanges?.();
        }}
        className={`w-full text-left p-5 rounded-3xl border-2 transition-all flex items-center gap-4 ${
          progress?.no_significant_changes
            ? "border-sage-500 bg-sage-500/10"
            : "border-paper-200 bg-white hover:border-sage-500/50 hover:bg-sage-500/5"
        }`}
      >
        <span className="w-11 h-11 rounded-2xl bg-sage-500/10 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-sage-700" strokeWidth={1.75} />
        </span>
        <span className="flex-1">
          <span className="block font-medium text-ink-900">No significant changes</span>
          <span className="block text-xs text-ink-500 mt-0.5">Client is stable since last visit — continue straight to today's treatment</span>
        </span>
        <ArrowRight className="w-4 h-4 text-ink-500 shrink-0" />
      </button>

      <div className="flex items-center gap-3 text-xs text-ink-500">
        <span className="flex-1 h-px bg-paper-200" /> or record what changed <span className="flex-1 h-px bg-paper-200" />
      </div>

      {/* How long did the benefit last? */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="benefit-duration-card">
        <div className="label-overline mb-1 text-sage-700">Last session's results</div>
        <h2 className="font-heading text-lg text-ink-900 mb-4">How long did the benefit last?</h2>
        <div className="flex flex-wrap gap-2">
          {BENEFIT_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              data-testid={`benefit-chip-${chip.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              onClick={() => { setField("benefit_duration", progress?.benefit_duration === chip ? "" : chip); onBlurSave?.(); }}
              className={`min-h-[38px] px-4 rounded-full text-sm border transition-colors ${
                progress?.benefit_duration === chip
                  ? "bg-ink-900 text-white border-ink-900"
                  : "bg-white border-paper-200 text-ink-700 hover:bg-paper-100"
              }`}
            >
              {chip}
            </button>
          ))}
        </div>
      </div>

      {/* CLIENT-REPORTED status — only things the client can actually perceive */}
      {clientCandidates.length > 0 && (
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="finding-updates-card">
          <div className="label-overline mb-1 text-sage-700">Client-reported — how they feel</div>
          <h2 className="font-heading text-lg text-ink-900 mb-1">Since last visit, how is it now?</h2>
          <p className="text-sm text-ink-500 mb-5">Based on what the client tells you. These become part of TODAY's record; last session's notes are never changed.</p>
          <div className="space-y-3">
            {clientCandidates.map((label) => (
              <div key={label} className="flex flex-wrap items-center gap-2" data-testid={`finding-row-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}`}>
                <span className="text-sm text-ink-900 flex-1 min-w-[140px]">{label}</span>
                <span className="flex gap-1.5">
                  {STATUSES.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setStatus(label, s.key)}
                      className={`min-h-[32px] px-3 rounded-full text-xs border transition-colors ${
                        statusOf(label) === s.key ? s.cls + " border" : "bg-white border-paper-200 text-ink-500 hover:bg-paper-100"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </span>
              </div>
            ))}
            {updates.filter((u) => u.source === "new").map((u) => (
              <div key={u.label} className="flex items-center gap-2 text-sm" data-testid="new-finding-row">
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wider">New</span>
                <span className="text-ink-900">{u.label}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-5">
            <input
              data-testid="add-finding-input"
              value={newFinding}
              onChange={(e) => setNewFinding(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addFinding()}
              placeholder="Add a new symptom, concern, or injury the client mentioned…"
              className="flex-1 min-h-[42px] px-4 rounded-full bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm text-ink-900"
            />
            <button
              type="button"
              data-testid="add-finding-btn"
              onClick={addFinding}
              className="min-h-[42px] px-4 rounded-full bg-ink-900 text-white text-sm inline-flex items-center gap-1.5 shrink-0"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>
      )}

      {/* THERAPIST REASSESSMENT — reference/checklist only, NO status buttons */}
      {reassessCandidates.length > 0 && (
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="reassess-card">
          <div className="label-overline mb-1 text-amber-700">Therapist — reference only</div>
          <h2 className="font-heading text-lg text-ink-900 mb-1">Reassess from Last Visit</h2>
          <p className="text-sm text-ink-500 mb-5">Structures addressed last time — a reminder of what may need reassessment today. Record your actual findings later in assessment, clinical capture, or ROM.</p>
          <div className="flex flex-wrap gap-2" data-testid="reassess-chips">
            {reassessCandidates.map((label) => (
              <span
                key={label}
                data-testid={`reassess-chip-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-paper-100 border border-paper-200 text-ink-700 text-sm"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* What changed — reuse the existing five-field widget */}
      <ProgressSinceLast progress={progress} onChange={(p) => onChange({ ...p, no_significant_changes: false })} onBlurSave={onBlurSave} />

      {/* New treatment priority */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="new-priority-card">
        <div className="label-overline mb-1 text-terracotta-600">Today's focus</div>
        <h2 className="font-heading text-lg text-ink-900 mb-4">New treatment priority (optional)</h2>
        <VoiceTextarea
          value={progress?.new_priority || ""}
          onChange={(v) => setField("new_priority", v)}
          onBlur={onBlurSave}
          rows={2}
          placeholder='"Focus on right shoulder today — new stiffness after tennis."'
          testId="new-priority-input"
        />
      </div>
    </div>
  );
}
